import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Clock, Loader2, AlertTriangle, ArrowRight, Search, Package, Gift, ExternalLink, MessageSquare, Send, Mail, Calendar, CircleDollarSign, Paperclip } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ClientWorkflows from "../components/ClientWorkflows";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR_RATE = 0.92;
const fmt = (amount, currency) => currency === "eur" ? `€${(amount * EUR_RATE).toFixed(0)}` : `$${amount?.toFixed(0)}`;
const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

const STORAGE_KEY = (pid) => `lf_track_email_${pid}`;

const STATUS_STEPS = ["pending", "ai_magicians", "human_review", "pending_feedback", "applying_feedback", "final_review", "delivered"];
const STATUS_LABELS = {
  awaiting_deposit: "Awaiting Deposit",
  pending: "Pending",
  ai_magicians: "AI Magicians Working",
  human_review: "Human Review",
  pending_feedback: "Pending Feedback",
  applying_feedback: "Applying Feedback",
  final_review: "Final Review",
  delivered: "Delivered",
  rejected: "Rejected",
};
const STATUS_COLORS = {
  awaiting_deposit: "text-amber-700 bg-amber-50 border-amber-200",
  pending: "text-blue-700 bg-blue-50 border-blue-200",
  ai_magicians: "text-purple-700 bg-purple-50 border-purple-200",
  human_review: "text-indigo-700 bg-indigo-50 border-indigo-200",
  pending_feedback: "text-amber-700 bg-amber-50 border-amber-200",
  applying_feedback: "text-orange-700 bg-orange-50 border-orange-200",
  final_review: "text-rose-700 bg-rose-50 border-rose-200",
  delivered: "text-emerald-700 bg-emerald-50 border-emerald-200",
  rejected: "text-red-700 bg-red-50 border-red-200",
};

const StatusProgress = ({ status }) => {
  const currentIndex = STATUS_STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="status-progress">
      {STATUS_STEPS.map((s, i) => {
        const done = i <= currentIndex;
        const active = i === currentIndex;
        return (
          <div key={s} className="flex items-center gap-1 flex-shrink-0">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all
              ${done ? "bg-zinc-950 border-zinc-950 text-white" : "bg-white border-zinc-200 text-zinc-400"}`}>
              {done && !active && <CheckCircle size={11} />}
              {active && <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
              {STATUS_LABELS[s]}
            </div>
            {i < STATUS_STEPS.length - 1 && <div className={`h-0.5 w-4 flex-shrink-0 ${i < currentIndex ? "bg-zinc-950" : "bg-zinc-200"}`} />}
          </div>
        );
      })}
    </div>
  );
};

function TrackForm({ onSearch }) {
  const [id, setId] = useState("");
  return (
    <div className="max-w-lg mx-auto text-center">
      <div className="inline-flex items-center justify-center w-14 h-14 bg-zinc-100 rounded-2xl mb-6">
        <Search size={24} className="text-zinc-600" />
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight text-zinc-950 mb-2">Track Your Project</h1>
      <p className="text-zinc-500 text-sm mb-8">Enter your project ID to check the status of your MVP build.</p>
      <div className="flex gap-3">
        <input data-testid="input-project-id" value={id} onChange={e => setId(e.target.value)}
          onKeyDown={e => e.key === "Enter" && id.trim() && onSearch(id.trim())}
          className="flex-1 px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-zinc-900 focus:outline-none text-sm"
          placeholder="proj_abc123..." />
        <button data-testid="btn-track-search" onClick={() => onSearch(id.trim())} disabled={!id.trim()}
          className="px-5 py-3 bg-zinc-950 text-white rounded-xl text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-colors">
          <ArrowRight size={16} />
        </button>
      </div>
      <p className="text-xs text-zinc-400 mt-3">Your project ID was included in your confirmation email.</p>
    </div>
  );
}

function ClientChat({ projectId }) {
  const [email, setEmail] = useState(() => sessionStorage.getItem(STORAGE_KEY(projectId)) || "");
  const [verified, setVerified] = useState(false);
  const [messages, setMessages] = useState([]);
  const [filesByMsg, setFilesByMsg] = useState({});
  const [content, setContent] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const load = async (e) => {
    if (!e) return;
    setLoading(true); setError("");
    try {
      const [m, f] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/messages`, { params: { email: e } }),
        axios.get(`${API}/projects/${projectId}/files`, { params: { email: e } }).catch(() => ({ data: [] })),
      ]);
      setMessages(m.data || []);
      const byMsg = {};
      (f.data || []).forEach(file => {
        const key = file.message_id || "_unattached";
        (byMsg[key] = byMsg[key] || []).push(file);
      });
      setFilesByMsg(byMsg);
      setVerified(true);
      sessionStorage.setItem(STORAGE_KEY(projectId), e);
    } catch (err) {
      setError(err.response?.status === 403 ? "That email doesn't match this project. Please use the email used to submit the project." : "Failed to load messages.");
      setVerified(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (email) load(email); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (verified) endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, verified]);

  const verify = async (e) => { e?.preventDefault(); setVerifying(true); await load(email); setVerifying(false); };

  const onPickFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!/\.(zip|pdf)$/i.test(f.name)) { alert("Only .zip or .pdf"); e.target.value = ""; return; }
    if (f.size > 25 * 1024 * 1024) { alert("Max 25 MB"); e.target.value = ""; return; }
    setPendingFile(f);
  };

  const send = async () => {
    if ((!content.trim() && !pendingFile) || sending) return;
    setSending(true);
    try {
      let messageId = null;
      if (content.trim()) {
        const res = await axios.post(`${API}/projects/${projectId}/messages`, { email, content: content.trim() });
        messageId = res.data?.message_id;
      }
      if (pendingFile) {
        const fd = new FormData();
        fd.append("email", email);
        fd.append("file", pendingFile);
        if (messageId) fd.append("message_id", messageId);
        await axios.post(`${API}/projects/${projectId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setContent(""); setPendingFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load(email);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send.");
    } finally { setSending(false); }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/files/${file.file_id}/download`, { params: { email }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = file.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Download failed"); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-2xl mt-5 overflow-hidden" data-testid="client-chat-card">
      <div className="px-5 py-4 border-b border-zinc-100 flex items-center gap-2">
        <MessageSquare size={16} className="text-zinc-700" />
        <span className="font-semibold text-zinc-900 text-sm">Project Chat</span>
        <span className="ml-auto text-xs text-zinc-500">Send change requests, questions, or feedback</span>
      </div>

      {!verified ? (
        <form onSubmit={verify} className="p-5" data-testid="chat-verify-form">
          <p className="text-xs text-zinc-600 mb-3 inline-flex items-center gap-1.5"><Mail size={12} /> Enter the email used to submit this project to view and post messages.</p>
          <div className="flex gap-2">
            <input data-testid="input-chat-email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
            <button type="submit" disabled={!email || verifying} data-testid="btn-verify-chat-email"
              className="px-4 py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-xl hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center gap-1.5">
              {verifying ? <Loader2 size={14} className="animate-spin" /> : <>Open chat <ArrowRight size={14} /></>}
            </button>
          </div>
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-3" data-testid="chat-error">{error}</div>}
        </form>
      ) : (
        <>
          <div className="p-5 space-y-3 bg-zinc-50/50 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6"><Loader2 size={18} className="animate-spin text-zinc-400" /></div>
            ) : messages.length === 0 ? (
              <div className="text-center text-zinc-400 text-sm py-6">No messages yet — start the conversation with our team.</div>
            ) : messages.map(m => {
              const attached = filesByMsg[m.message_id] || [];
              return (
                <div key={m.message_id} data-testid={`chat-msg-${m.message_id}`}
                  className={`max-w-[85%] ${m.sender === "client" ? "ml-auto" : ""}`}>
                  <div className={`text-[10px] font-semibold uppercase tracking-wider mb-1 ${m.sender === "client" ? "text-right text-zinc-700" : "text-zinc-500"}`}>
                    {m.sender_name} · {fmtDateTime(m.created_at)}
                  </div>
                  <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.sender === "client" ? "bg-zinc-950 text-white rounded-br-sm" : "bg-white border border-zinc-200 text-zinc-800 rounded-bl-sm"
                  }`}>{m.content}</div>
                  {attached.map(f => (
                    <button key={f.file_id} data-testid={`btn-chat-file-${f.file_id}`} onClick={() => downloadFile(f)}
                      className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border ${
                        m.sender === "client" ? "bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700" : "bg-white border-zinc-200 text-zinc-800 hover:bg-zinc-50"
                      }`}>
                      <Paperclip size={11} /> {f.filename}
                    </button>
                  ))}
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="border-t border-zinc-100 p-3 space-y-2">
            {pendingFile && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs" data-testid="client-pending-file">
                <Paperclip size={12} className="text-blue-600" />
                <span className="font-semibold text-blue-900 truncate flex-1">{pendingFile.name}</span>
                <button onClick={() => { setPendingFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-blue-600">×</button>
              </div>
            )}
            <div className="flex gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.zip" onChange={onPickFile} className="hidden" data-testid="input-client-file" />
              <button data-testid="btn-attach-client-file" onClick={() => fileRef.current?.click()} disabled={sending}
                className="px-3 py-2 bg-zinc-100 text-zinc-700 rounded-xl hover:bg-zinc-200 disabled:opacity-40 self-end" title="Attach .zip or .pdf">
                <Paperclip size={14} />
              </button>
              <textarea data-testid="input-client-chat" value={content} onChange={e => setContent(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
                placeholder="Ask a question, request a change… (⌘/Ctrl+Enter to send)" rows={2}
                className="flex-1 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900 resize-none" />
              <button data-testid="btn-send-client-chat" onClick={send} disabled={(!content.trim() && !pendingFile) || sending}
                className="px-4 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-xl hover:bg-zinc-800 disabled:opacity-40 self-end inline-flex items-center gap-1.5">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Send</>}
              </button>
            </div>
          </div>
          {error && <div className="text-xs text-red-600 bg-red-50 border-t border-red-200 px-5 py-2">{error}</div>}
        </>
      )}
    </div>
  );
}

function AdditionalCharges({ chargesFromTrack, projectId, email, currency }) {
  const [charges, setCharges] = useState(chargesFromTrack || []);
  const [loading, setLoading] = useState(!chargesFromTrack);

  // If charges came from the /track endpoint, use them directly.
  // Otherwise (admin or authenticated customer) fall back to the email-gated endpoint.
  useEffect(() => {
    if (chargesFromTrack) { setCharges(chargesFromTrack); setLoading(false); return; }
    if (!email) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}/charges`, { params: { email } });
        if (!cancelled) setCharges(res.data || []);
      } catch (err) { console.warn("AdditionalCharges: failed to fetch charges", err); /* email mismatch or no charges */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [projectId, email, chargesFromTrack]);

  if (loading || !charges.length) return null;
  const pending = charges.filter(c => c.status === "pending");
  const paid = charges.filter(c => c.status === "paid");

  return (
    <div data-testid="track-charges-card" className="bg-white border border-zinc-200 rounded-2xl p-5 mt-5">
      <h3 className="font-semibold text-zinc-900 text-sm mb-3 flex items-center gap-2">
        <CircleDollarSign size={16} className="text-zinc-700" /> Additional Charges
      </h3>
      <div className="space-y-2">
        {pending.map(c => (
          <div key={c.charge_id} data-testid={`charge-row-${c.charge_id}`} className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-zinc-950">{fmt(c.amount_usd, c.currency)}</div>
              <div className="text-xs text-zinc-700 mt-0.5">{c.description}</div>
              {c.payment_required_before_delivery && <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900 mt-1">Required before delivery</div>}
            </div>
            <a href={c.payment_url} target="_blank" rel="noopener noreferrer" data-testid={`btn-pay-charge-${c.charge_id}`}
              className="inline-flex items-center gap-1.5 bg-zinc-950 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-zinc-800">
              Pay {fmt(c.amount_usd, c.currency)} <ArrowRight size={11} />
            </a>
          </div>
        ))}
        {paid.map(c => (
          <div key={c.charge_id} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-zinc-950">{fmt(c.amount_usd, c.currency)}</div>
              <div className="text-xs text-zinc-700 mt-0.5">{c.description}</div>
            </div>
            <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700">
              <CheckCircle size={12} /> Paid
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProjectStatus({ data }) {
  const { project, proposal } = data;
  const price = project.price_override || proposal?.delivery_adjusted_price_usd;
  const currency = project.currency || "usd";
  const previewUrl = data.preview_url || project.preview_url;

  return (
    <div className="max-w-2xl mx-auto" data-testid="project-status-card">
      <div className="bg-white border border-zinc-200 rounded-2xl overflow-hidden mb-5">
        <div className="px-6 py-5 border-b border-zinc-100 flex items-start justify-between">
          <div>
            <div className="text-xs text-zinc-500 font-medium mb-1">{project.project_id}</div>
            <h2 className="text-xl font-bold text-zinc-950">{project.industry} MVP</h2>
            <div className="text-sm text-zinc-500 mt-0.5">{project.delivery_weeks}-week delivery · Submitted {new Date(project.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
            {project.due_date && (
              <div className="text-xs text-amber-700 bg-amber-50 inline-flex items-center gap-1 px-2 py-0.5 rounded-full mt-2 font-semibold" data-testid="track-due-date">
                <Calendar size={11} /> Target delivery: {fmtDate(project.due_date)}
              </div>
            )}
            {project.start_date && (
              <div className="text-xs text-blue-700 bg-blue-50 inline-flex items-center gap-1 px-2 py-0.5 rounded-full mt-2 ml-2 font-semibold" data-testid="track-start-date">
                <Calendar size={11} /> Work starts: {fmtDate(project.start_date)}
              </div>
            )}
          </div>
          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border flex-shrink-0 ml-4 ${STATUS_COLORS[project.status] || STATUS_COLORS.pending}`}>
            {STATUS_LABELS[project.status] || project.status}
          </span>
        </div>
        <div className="px-6 py-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Build Progress</div>
          <StatusProgress status={project.status} />
        </div>
      </div>

      {previewUrl && (
        <a href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid="track-preview-url-banner"
          className="block bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 rounded-2xl p-5 mb-5 hover:shadow-md transition-shadow group">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
              <ExternalLink size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 inline-block px-2 py-0.5 rounded-full mb-1">Live preview ready</div>
              <div className="font-bold text-zinc-950">View your MVP →</div>
              <div className="text-xs text-zinc-600 mt-0.5 truncate">{previewUrl}</div>
            </div>
          </div>
        </a>
      )}

      {project.status === "final_review" && project.final_review_deadline && (() => {
        const deadline = new Date(project.final_review_deadline);
        const ms = deadline - new Date();
        const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
        const overdue = ms < 0;
        // Early-pay 48h discount window
        const epDeadline = project.early_pay_deadline ? new Date(project.early_pay_deadline) : null;
        const epActive = epDeadline && epDeadline > new Date();
        const epHours = epActive ? Math.max(0, Math.floor((epDeadline - new Date()) / (1000 * 60 * 60))) : 0;
        const fullFinal = price ? price * 0.5 : 0;
        const discounted = epActive ? fullFinal * 0.97 : fullFinal;
        const savings = epActive ? fullFinal * 0.03 : 0;

        const handlePayEarly = async () => {
          try {
            const res = await axios.post(`${API}/projects/${project.project_id}/pay-final`, { origin_url: window.location.origin });
            if (res.data?.url) window.location.href = res.data.url;
          } catch (e) { alert(e.response?.data?.detail || "Could not start checkout"); }
        };

        return (
          <div data-testid="track-final-review-banner"
            className={`rounded-2xl p-5 mb-5 border-2 ${overdue ? "bg-red-50 border-red-300" : "bg-gradient-to-br from-rose-50 via-white to-amber-50 border-rose-200"}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${overdue ? "bg-red-600" : "bg-rose-600"}`}>
                <AlertTriangle size={18} className="text-white" />
              </div>
              <div className="flex-1">
                <div className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${overdue ? "bg-red-200 text-red-900" : "bg-rose-200 text-rose-900"}`}>
                  Final Review · Payment Required
                </div>
                <div className="text-lg font-extrabold text-zinc-950 mt-1">
                  {overdue ? `Overdue by ${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"} to pay the final 50%`}
                </div>
                <p className="text-sm text-zinc-700 mt-1">
                  Your MVP is ready for delivery. Pay the remaining 50% within 14 days of entering Final Review to unlock your application.
                </p>

                {epActive && (
                  <div data-testid="early-pay-badge" className="mt-3 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                    <div className="text-lg">⚡</div>
                    <div className="flex-1">
                      <div className="font-bold text-emerald-900 text-sm">Pay early, save {fmt(savings, currency)} (3% off)</div>
                      <div className="text-xs text-emerald-800 mt-0.5">
                        Limited offer — pay within the next <strong>{epHours} hour{epHours === 1 ? "" : "s"}</strong> and we'll discount your final invoice from {fmt(fullFinal, currency)} to <strong>{fmt(discounted, currency)}</strong>.
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {epActive ? (
                    <button onClick={handlePayEarly} data-testid="btn-pay-early"
                      className="inline-flex items-center gap-2 bg-emerald-600 text-white font-bold text-sm px-5 py-2.5 rounded-full hover:bg-emerald-700 transition-colors">
                      ⚡ Pay {fmt(discounted, currency)} now <ArrowRight size={14} />
                    </button>
                  ) : project.final_payment_url && (
                    <a href={project.final_payment_url} target="_blank" rel="noopener noreferrer" data-testid="btn-final-pay"
                      className="inline-flex items-center gap-2 bg-zinc-950 text-white font-bold text-sm px-5 py-2.5 rounded-full hover:bg-zinc-800 transition-colors">
                      Pay Final {fmt(fullFinal, currency)} <ArrowRight size={14} />
                    </a>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-2">Deadline: {deadline.toLocaleDateString(undefined, { dateStyle: "full" })}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {proposal && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-zinc-200 rounded-2xl p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Project Scope</div>
            <p className="text-sm text-zinc-700 leading-relaxed mb-3">{proposal.project_summary}</p>
            <div className="flex flex-wrap gap-1.5">
              {proposal.features_list?.slice(0, 5).map((f, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded-full">{f}</span>
              ))}
            </div>
          </div>
          <div className="bg-white border border-zinc-200 rounded-2xl p-5">
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Pricing Summary</div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-zinc-500">Total project price</span><span className="font-bold text-zinc-950" data-testid="track-total-price">{price ? fmt(price, currency) : "—"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">50% upfront</span><span className="font-semibold text-zinc-800">{price ? fmt(price * 0.5, currency) : "—"}</span></div>
              <div className="flex justify-between pt-2 border-t border-zinc-100"><span className="text-zinc-500">50% on delivery</span><span className="font-semibold text-zinc-800">{price ? fmt(price * 0.5, currency) : "—"}</span></div>
            </div>
            {proposal.bonus_addon && (
              <div data-testid="track-bonus-line" className="mt-4 pt-4 border-t border-emerald-100 flex items-start gap-2 text-xs text-emerald-800 bg-emerald-50 -mx-5 -mb-5 px-5 py-3 rounded-b-2xl">
                <Gift size={13} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                <span><strong>Bonus included:</strong> {proposal.bonus_addon.name} — {proposal.bonus_addon.tagline}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {project.status === "in_progress" && (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Package size={18} className="text-purple-600 mt-0.5" />
            <div>
              <div className="font-semibold text-purple-800 text-sm">Your MVP is being built</div>
              <div className="text-purple-700 text-xs mt-1">Our team is actively working on your project. You'll be notified when it's ready for final review and delivery.</div>
            </div>
          </div>
        </div>
      )}

      {project.status === "delivered" && (
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <CheckCircle size={18} className="text-green-600 mt-0.5" />
            <div>
              <div className="font-semibold text-green-800 text-sm">Your MVP has been delivered</div>
              <div className="text-green-700 text-xs mt-1">Check your email for access details, documentation, and your recommended development partner information.</div>
            </div>
          </div>
        </div>
      )}

      {project.admin_notes && (
        <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-2xl p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Note from the Team</div>
          <p className="text-sm text-zinc-700">{project.admin_notes}</p>
        </div>
      )}

      <AdditionalCharges projectId={project.project_id} currency={currency}
        chargesFromTrack={data.additional_charges}
        email={sessionStorage.getItem(STORAGE_KEY(project.project_id)) || ""} />

      {sessionStorage.getItem(STORAGE_KEY(project.project_id)) && (
        <div className="mt-5 space-y-5">
          <ClientWorkflows
            project={project}
            email={sessionStorage.getItem(STORAGE_KEY(project.project_id))}
            onChange={() => window.location.reload()}
          />
        </div>
      )}

      <ClientChat projectId={project.project_id} />
    </div>
  );
}

export default function TrackProject() {
  const { projectId: urlProjectId } = useParams();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(urlProjectId || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (id) => {
    setLoading(true); setError(""); setData(null);
    try {
      const res = await axios.get(`${API}/projects/${id}/track`);
      setData(res.data);
      if (id !== urlProjectId) navigate(`/track/${id}`, { replace: true });
    } catch (e) {
      setError(e.response?.status === 404 ? "Project not found. Please check your project ID." : "Failed to load project. Please try again.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (urlProjectId) search(urlProjectId); }, [urlProjectId]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="track-project-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-24">
        {!data && !loading && <TrackForm onSearch={search} />}
        {loading && (
          <div className="text-center py-12">
            <Loader2 size={32} className="animate-spin text-zinc-400 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Loading project status...</p>
          </div>
        )}
        {error && (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-2xl mb-4">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <p className="text-zinc-700 mb-4">{error}</p>
            <button onClick={() => { setData(null); setError(""); navigate("/track"); }}
              className="px-5 py-2.5 bg-zinc-950 text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              Search Again
            </button>
          </div>
        )}
        {data && <ProjectStatus data={data} />}
        {data && (
          <div className="text-center mt-6">
            <button onClick={() => { setData(null); setError(""); navigate("/track"); }}
              data-testid="btn-track-new" className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors underline">
              Track a different project
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
